/**
 * Reissue a sent final balance: the original stays on record as Void (its customer link stops
 * working), and a replacement balance for the same amount is raised under the next invoice
 * ref. Agreed deductions are then added to the replacement before it goes to FreeAgent.
 */
import { directSupabase } from '../../lib/supabaseClient';
import * as freeAgentApi from '../../freeagent/api';
import { documentsService } from '../../modules/docs/services/documents.service';
import { nullifyDocumentShareLink } from '../../modules/docs/services/documentShareLinks.service';
import type { DocumentWithLines } from '../../modules/docs/types/documents.types';
import {
  BALANCE_REISSUE_EVENT_PREFIX,
  balanceReissueAllowed,
  deductionAmount,
  documentDisplayRef,
  isDeductionLine,
} from '../../modules/docs/utils/invoiceLedger';
import { emitActivityEventBackground } from '../../services/activityEvents.service';
import { paymentScheduleService } from './paymentSchedule.service';

function isNotFound(error: any): boolean {
  return error?.status === 404 || String(error?.message || '').includes('404');
}

/** The original must be gone from FreeAgent first, or the customer could pay the old amount there. */
async function assertGoneFromFreeAgent(invoiceId: string, ref: string): Promise<void> {
  const { data, error } = await directSupabase
    .from('documents')
    .select('freeagent_invoice_url')
    .eq('id', invoiceId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  const url = String((data as any)?.freeagent_invoice_url || '').trim();
  if (!url) return;
  try {
    await freeAgentApi.getInvoice(url);
  } catch (err) {
    if (isNotFound(err)) return;
    throw new Error(`JARVIS could not check FreeAgent for ${ref}. Connect FreeAgent and try again.`);
  }
  throw new Error(`${ref} is still in FreeAgent. Delete it there first, then reissue.`);
}

export async function reissueBalanceInvoice(invoiceId: string): Promise<DocumentWithLines> {
  const original = await documentsService.getDocument(invoiceId);
  if (!original) throw new Error('Invoice not found');
  const ref = documentDisplayRef(original) || 'This invoice';
  const schedule = original.projectId ? await paymentScheduleService.getByProject(String(original.projectId)) : null;
  if (!balanceReissueAllowed({ invoice: original, stages: schedule?.stages || null })) {
    throw new Error('Only a sent final balance can be reissued.');
  }
  await assertGoneFromFreeAgent(original.id, ref);

  const balanceLines = (original.lines || []).filter((line) => !isDeductionLine(line));
  // A reissue of a reissue starts from the balance before deductions and carries them across
  const carriedDeductions = (original.lines || [])
    .filter((line) => isDeductionLine(line))
    .map((line) => ({ name: line.name, amount: deductionAmount(line) }));
  const deducted = carriedDeductions.reduce((sum, item) => sum + item.amount, 0);
  // An older balance can carry every quote line; the replacement bills it as one balance line
  const singleLine = balanceLines.length === 1 ? balanceLines[0] : null;
  const replacement = await documentsService.createRoleInvoice({
    source: original,
    role: 'works',
    amount: Math.round((Number(original.grossTotal || 0) + deducted) * 100) / 100,
    title: original.title,
    lineName: singleLine?.name || 'Balance due',
    lineDescription: singleLine?.description ?? null,
    sourceSurveyEvent: `${BALANCE_REISSUE_EVENT_PREFIX}${original.id}`,
    depositAmount: 0,
    depositPercentage: null,
    hasDeposit: false,
    // Its amount is the issued balance less deductions, never the quote sync's
    freeze: true,
    sourceHeader: { vat_enabled: original.vatEnabled, vat_pct: original.vatPct },
  });

  const stage = schedule?.stages.find((item) => item.invoiceId === original.id) || null;
  let carried = replacement;
  try {
    if (carriedDeductions.length) {
      carried = await documentsService.setBalanceDeductions(replacement.id, carriedDeductions);
    }
    if (schedule && stage) {
      await paymentScheduleService.bindIssuedStage({
        schedule,
        stageId: stage.id,
        invoiceId: replacement.id,
        issuedAmount: Number(carried.grossTotal || original.grossTotal || 0),
      });
    }
    await documentsService.updateDocument(original.id, { status: 'void' });
  } catch (err) {
    // Leave things as they were: the original stays live and the replacement goes
    if (schedule && stage) {
      await paymentScheduleService.bindIssuedStage({
        schedule,
        stageId: stage.id,
        invoiceId: original.id,
        issuedAmount: Number(stage.issuedAmount ?? original.grossTotal ?? 0),
      }).catch(() => undefined);
    }
    await documentsService.deleteDocument(replacement.id).catch(() => undefined);
    throw err;
  }

  const replacementRef = documentDisplayRef(replacement) || 'the replacement';
  const { data: links } = await directSupabase
    .from('document_share_links')
    .select('id')
    .eq('document_id', original.id)
    .is('revoked_at', null);
  await Promise.all((links || []).map((link: { id: string }) => (
    nullifyDocumentShareLink(link.id, `Balance reissued as ${replacementRef}`).catch(() => undefined)
  )));

  if (original.projectId) {
    emitActivityEventBackground({
      action: 'document.created',
      entityType: 'document',
      entityId: replacement.id,
      projectId: String(original.projectId),
      label: `Balance ${ref} reissued as ${replacementRef}`,
      metadata: { reissueOf: original.id, kind: 'Invoice', invoiceRef: replacement.invoiceRef || null },
    });
  }
  return carried;
}
